import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router';
import { intelligenceApi, workspacesApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Alert } from '@rankhelm/ui';
import type { Workspace, IntelligenceReport, IntelligenceReportVersion } from '@rankhelm/shared';

/** Extended report type with joined versions from the API */
interface IntelReport extends IntelligenceReport {
  versions?: IntelReportVersion[];
  current_version?: IntelReportVersion;
}

interface IntelReportVersion extends IntelligenceReportVersion {
  version_number: number;
}

interface ReportContent {
  status?: string;
  error?: string;
  message?: string;
  market_overview?: {
    summary: string;
    market_size_indicators: string[];
    key_trends: string[];
  };
  business_analysis?: {
    strengths: string[];
    weaknesses: string[];
    unique_positioning: string;
    target_audiences: { segment: string; description: string; intent: string; content_needs: string }[];
  };
  competitor_analysis?: {
    overview: string;
    competitors: {
      name: string;
      url: string;
      strengths: string[];
      weaknesses: string[];
      content_strategy: string;
      estimated_authority: string;
    }[];
  };
  keyword_opportunities?: {
    overview: string;
    head_terms: { keyword: string; intent: string; difficulty: string; priority: string }[];
    mid_tail: { keyword: string; intent: string; difficulty: string; priority: string }[];
    long_tail_questions: { keyword: string; intent: string; difficulty: string; priority: string }[];
    local_keywords: { keyword: string; intent: string; difficulty: string; priority: string }[];
  };
  content_gaps?: {
    overview: string;
    gaps: { topic: string; description: string; opportunity_level: string; recommended_format: string }[];
  };
  audience_insights?: {
    journey_stages: { stage: string; questions: string[]; content_types: string[]; triggers: string[] }[];
    pain_points: string[];
    objections: string[];
  };
  strategic_recommendations?: {
    immediate_actions: string[];
    content_pillars_suggested: { name: string; description: string; rationale: string; estimated_articles: number }[];
    quick_wins: string[];
    long_term_plays: string[];
  };
  citations?: string[];
}

const BADGE_COLORS: Record<string, string> = {
  draft: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-blue-500/20 text-blue-400',
  locked: 'bg-green-500/20 text-green-400',
};

const PRIORITY_COLORS: Record<string, string> = {
  high: 'text-red-400',
  medium: 'text-yellow-400',
  low: 'text-green-400',
};

const DIFFICULTY_COLORS: Record<string, string> = {
  high: 'bg-red-500/20 text-red-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  low: 'bg-green-500/20 text-green-400',
};

export function IntelligenceReportPage() {
  const { workspaceId } = useParams();
  const { user } = useAuth();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [report, setReport] = useState<IntelReport | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [content, setContent] = useState<ReportContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  // Feedback state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  // Polling state
  const [isPolling, setIsPolling] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [wsRes, intelRes] = await Promise.all([
        workspacesApi.get(workspaceId!),
        intelligenceApi.get(workspaceId!).catch(() => ({ data: null })),
      ]);
      setWorkspace(wsRes.data);
      setReport(intelRes.data as IntelReport | null);

      if (intelRes.data) {
        const currentVersionId = intelRes.data.current_version_id;
        setSelectedVersionId(currentVersionId || '');

        // Parse content from current version
        const versions = (intelRes.data as IntelReport).versions;
        const currentVersion = versions?.find(
          (v) => v.id === currentVersionId
        );
        if (currentVersion) {
          try {
            const parsed = JSON.parse(currentVersion.content_json || '{}');
            setContent(parsed);

            // Check if still generating
            if (parsed.status === 'generating') {
              setIsPolling(true);
            } else {
              setIsPolling(false);
            }
          } catch {
            setContent(null);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Polling effect
  useEffect(() => {
    if (!isPolling) return;
    const interval = setInterval(() => {
      loadData();
    }, 3000);
    return () => clearInterval(interval);
  }, [isPolling, loadData]);

  const handleVersionSwitch = async (versionId: string) => {
    setSelectedVersionId(versionId);
    const version = report?.versions?.find((v) => v.id === versionId);
    if (version) {
      try {
        setContent(JSON.parse(version.content_json || '{}'));
      } catch {
        setContent(null);
      }
    }
  };

  const handleApprove = async () => {
    setActionLoading('approve');
    setError('');
    try {
      await intelligenceApi.update(report!.id, { status: 'approved' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading('');
    }
  };

  const handleLock = async () => {
    setActionLoading('lock');
    setError('');
    try {
      await intelligenceApi.update(report!.id, { status: 'locked' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to lock');
    } finally {
      setActionLoading('');
    }
  };

  const handleUnlock = async () => {
    setActionLoading('unlock');
    setError('');
    try {
      await intelligenceApi.update(report!.id, { status: 'draft' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock');
    } finally {
      setActionLoading('');
    }
  };

  const handleFeedback = async () => {
    if (!feedbackText.trim()) return;
    setActionLoading('feedback');
    setError('');
    try {
      await intelligenceApi.feedback(report!.id, feedbackText.trim());
      setFeedbackText('');
      setShowFeedback(false);
      setIsPolling(true);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setActionLoading('');
    }
  };

  const handleRegenerate = async () => {
    setActionLoading('regenerate');
    setError('');
    try {
      await intelligenceApi.regenerate(report!.id);
      setIsPolling(true);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return <Spinner message="Loading intelligence report..." fullPage />;
  }

  if (!workspace) {
    return <div className="text-red-400">Workspace not found</div>;
  }

  const isAdmin = user?.role === 'super_admin' || user?.role === 'account_admin';
  const isGenerating = content?.status === 'generating';
  const isError = content?.status === 'error';
  const hasContent = content && !isGenerating && !isError && content.market_overview;
  const reportVersions = report?.versions || [];
  const sortedVersions = [...reportVersions].sort(
    (a, b) => b.version_number - a.version_number
  );

  return (
    <div className="max-w-5xl mx-auto">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Intelligence Report</h1>
          <p className="text-slate-400 text-sm mt-1">{workspace.name}</p>
        </div>
        {report && (
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${BADGE_COLORS[report.status as string] || 'bg-slate-500/20 text-slate-400'}`}>
              {report.status}
            </span>
          </div>
        )}
      </div>

      {!report && (
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-8 text-center">
          <p className="text-slate-400 mb-4">No intelligence report has been generated for this workspace yet.</p>
          <Link
            to={`/workspaces/${workspaceId}`}
            className="px-5 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Go to Workspace Dashboard
          </Link>
        </div>
      )}

      {report && (
        <>
          {/* Version Switcher + Actions */}
          <div className="bg-navy-900 border border-navy-700 rounded-xl p-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              {/* Version selector */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-400">Version:</label>
                <select
                  value={selectedVersionId}
                  onChange={(e) => handleVersionSwitch(e.target.value)}
                  className="px-3 py-1.5 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-helm-500"
                >
                  {sortedVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version_number}
                      {v.id === report.current_version_id ? ' (current)' : ''}
                      {v.feedback_prompt ? ' — feedback' : ''}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-slate-500">
                  {sortedVersions.length} version{sortedVersions.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex-1" />

              {/* Action buttons */}
              {isAdmin && !isGenerating && (
                <div className="flex items-center gap-2">
                  {report.status === 'draft' && hasContent && (
                    <>
                      <button
                        onClick={() => setShowFeedback(!showFeedback)}
                        className="px-3 py-1.5 bg-navy-800 hover:bg-navy-700 text-slate-300 text-xs rounded-lg border border-navy-600 transition-colors"
                      >
                        Request Changes
                      </button>
                      <button
                        onClick={handleApprove}
                        disabled={actionLoading === 'approve'}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
                      >
                        {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
                      </button>
                    </>
                  )}
                  {report.status === 'approved' && (
                    <>
                      <button
                        onClick={handleUnlock}
                        disabled={actionLoading === 'unlock'}
                        className="px-3 py-1.5 bg-navy-800 hover:bg-navy-700 text-slate-300 text-xs rounded-lg border border-navy-600 transition-colors disabled:opacity-50"
                      >
                        Back to Draft
                      </button>
                      <button
                        onClick={handleLock}
                        disabled={actionLoading === 'lock'}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
                      >
                        {actionLoading === 'lock' ? 'Locking...' : 'Lock & Generate Pillars'}
                      </button>
                    </>
                  )}
                  {report.status === 'locked' && (
                    <button
                      onClick={handleRegenerate}
                      disabled={actionLoading === 'regenerate'}
                      className="px-3 py-1.5 bg-navy-800 hover:bg-navy-700 text-slate-300 text-xs rounded-lg border border-navy-600 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'regenerate' ? 'Regenerating...' : 'Regenerate Full Report'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Feedback panel */}
            {showFeedback && (
              <div className="mt-4 pt-4 border-t border-navy-700">
                <h3 className="text-white text-sm font-medium mb-2">Request Changes</h3>
                <p className="text-xs text-slate-400 mb-3">
                  Describe what changes or additions you'd like. The system will conduct additional research and re-synthesize the report.
                </p>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="e.g., The report doesn't mention our SaaS pricing model. Please incorporate how our tiered pricing compares to competitors."
                  rows={3}
                  className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
                />
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={handleFeedback}
                    disabled={actionLoading === 'feedback' || !feedbackText.trim()}
                    className="px-5 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'feedback' ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                  <button
                    onClick={() => { setShowFeedback(false); setFeedbackText(''); }}
                    className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm rounded-lg border border-navy-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Generating state */}
          {isGenerating && (
            <div className="bg-navy-900 border border-helm-500/30 rounded-xl p-12 text-center">
              <div className="inline-block w-8 h-8 border-2 border-helm-500 border-t-transparent rounded-full animate-spin mb-4" />
              <h3 className="text-white text-lg font-semibold mb-2">Generating Intelligence Report</h3>
              <p className="text-slate-400 text-sm mb-2">{content?.message || 'Researching your market...'}</p>
              <p className="text-slate-500 text-xs">This typically takes 30-60 seconds. The page will update automatically.</p>
            </div>
          )}

          {/* Error state */}
          {isError && (
            <div className="bg-navy-900 border border-red-500/30 rounded-xl p-8 text-center">
              <h3 className="text-red-400 text-lg font-semibold mb-2">Generation Failed</h3>
              <p className="text-slate-400 text-sm mb-4">{content?.error || 'An error occurred during generation.'}</p>
              {isAdmin && (
                <button
                  onClick={handleRegenerate}
                  disabled={actionLoading === 'regenerate'}
                  className="px-5 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'regenerate' ? 'Retrying...' : 'Retry Generation'}
                </button>
              )}
            </div>
          )}

          {/* Report Content */}
          {hasContent && (
            <div className="space-y-6">
              {/* Market Overview */}
              {content.market_overview && (
                <ReportSection title="Market Overview" icon="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z">
                  <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{content.market_overview.summary}</p>
                  {content.market_overview.key_trends?.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Key Trends</h4>
                      <div className="space-y-1">
                        {content.market_overview.key_trends.map((trend, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                            <span className="text-helm-400 mt-0.5 shrink-0">&#9656;</span>
                            <span>{trend}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </ReportSection>
              )}

              {/* Business Analysis */}
              {content.business_analysis && (
                <ReportSection title="Business Analysis" icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="text-xs font-medium text-green-400 uppercase tracking-wider mb-2">Strengths</h4>
                      <div className="space-y-1">
                        {content.business_analysis.strengths?.map((s, i) => (
                          <div key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <span className="text-green-400 shrink-0">+</span>
                            <span>{s}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-red-400 uppercase tracking-wider mb-2">Weaknesses</h4>
                      <div className="space-y-1">
                        {content.business_analysis.weaknesses?.map((w, i) => (
                          <div key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <span className="text-red-400 shrink-0">-</span>
                            <span>{w}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {content.business_analysis.unique_positioning && (
                    <div className="bg-navy-800/50 rounded-lg p-4 mb-4">
                      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Unique Positioning</h4>
                      <p className="text-sm text-slate-300">{content.business_analysis.unique_positioning}</p>
                    </div>
                  )}
                  {content.business_analysis.target_audiences?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Target Audiences</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {content.business_analysis.target_audiences.map((a, i) => (
                          <div key={i} className="bg-navy-800/50 rounded-lg p-3">
                            <div className="text-sm font-medium text-white mb-1">{a.segment}</div>
                            <p className="text-xs text-slate-400">{a.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </ReportSection>
              )}

              {/* Competitor Analysis */}
              {content.competitor_analysis && (
                <ReportSection title="Competitor Analysis" icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                  <p className="text-slate-300 text-sm mb-4">{content.competitor_analysis.overview}</p>
                  <div className="space-y-3">
                    {content.competitor_analysis.competitors?.map((comp, i) => (
                      <div key={i} className="bg-navy-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">{comp.name}</span>
                            {comp.url && (
                              <span className="text-xs text-slate-500">{comp.url}</span>
                            )}
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            comp.estimated_authority === 'high' ? 'bg-red-500/20 text-red-400' :
                            comp.estimated_authority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {comp.estimated_authority} authority
                          </span>
                        </div>
                        {comp.content_strategy && (
                          <p className="text-xs text-slate-400 mb-2">{comp.content_strategy}</p>
                        )}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            {comp.strengths?.map((s, j) => (
                              <div key={j} className="text-green-400/80">+ {s}</div>
                            ))}
                          </div>
                          <div>
                            {comp.weaknesses?.map((w, j) => (
                              <div key={j} className="text-red-400/80">- {w}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ReportSection>
              )}

              {/* Keyword Opportunities */}
              {content.keyword_opportunities && (
                <ReportSection title="Keyword Opportunities" icon="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z">
                  <p className="text-slate-300 text-sm mb-4">{content.keyword_opportunities.overview}</p>
                  <KeywordTable label="Head Terms" keywords={content.keyword_opportunities.head_terms} />
                  <KeywordTable label="Mid-Tail Keywords" keywords={content.keyword_opportunities.mid_tail} />
                  <KeywordTable label="Long-Tail Questions" keywords={content.keyword_opportunities.long_tail_questions} />
                  <KeywordTable label="Local Keywords" keywords={content.keyword_opportunities.local_keywords} />
                </ReportSection>
              )}

              {/* Content Gaps */}
              {content.content_gaps && (
                <ReportSection title="Content Gaps" icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z">
                  <p className="text-slate-300 text-sm mb-4">{content.content_gaps.overview}</p>
                  <div className="space-y-2">
                    {content.content_gaps.gaps?.map((gap, i) => (
                      <div key={i} className="bg-navy-800/50 rounded-lg p-3 flex items-start gap-3">
                        <span className={`px-2 py-0.5 rounded text-xs shrink-0 ${
                          gap.opportunity_level === 'high' ? 'bg-green-500/20 text-green-400' :
                          gap.opportunity_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {gap.opportunity_level}
                        </span>
                        <div className="flex-1">
                          <div className="text-sm text-white font-medium">{gap.topic}</div>
                          <p className="text-xs text-slate-400">{gap.description}</p>
                        </div>
                        <span className="text-xs text-slate-500 shrink-0">{gap.recommended_format}</span>
                      </div>
                    ))}
                  </div>
                </ReportSection>
              )}

              {/* Audience Insights */}
              {content.audience_insights && (
                <ReportSection title="Audience Insights" icon="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z">
                  {content.audience_insights.pain_points?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Pain Points</h4>
                      <div className="flex flex-wrap gap-2">
                        {content.audience_insights.pain_points.map((p, i) => (
                          <span key={i} className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {content.audience_insights.journey_stages?.map((stage, i) => (
                    <div key={i} className="bg-navy-800/50 rounded-lg p-4 mb-3">
                      <h4 className="text-sm font-medium text-white mb-2 capitalize">{stage.stage}</h4>
                      <div className="grid md:grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="text-slate-400 block mb-1">Questions</span>
                          {stage.questions?.map((q, j) => (
                            <div key={j} className="text-slate-300 mb-0.5">{q}</div>
                          ))}
                        </div>
                        <div>
                          <span className="text-slate-400 block mb-1">Content Types</span>
                          {stage.content_types?.map((ct, j) => (
                            <div key={j} className="text-slate-300 mb-0.5">{ct}</div>
                          ))}
                        </div>
                        <div>
                          <span className="text-slate-400 block mb-1">Triggers</span>
                          {stage.triggers?.map((t, j) => (
                            <div key={j} className="text-slate-300 mb-0.5">{t}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </ReportSection>
              )}

              {/* Strategic Recommendations */}
              {content.strategic_recommendations && (
                <ReportSection title="Strategic Recommendations" icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4">
                  {content.strategic_recommendations.immediate_actions?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-medium text-helm-400 uppercase tracking-wider mb-2">Immediate Actions (30 days)</h4>
                      <div className="space-y-1">
                        {content.strategic_recommendations.immediate_actions.map((a, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                            <span className="text-helm-400 font-bold shrink-0">{i + 1}.</span>
                            <span>{a}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {content.strategic_recommendations.quick_wins?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-medium text-green-400 uppercase tracking-wider mb-2">Quick Wins</h4>
                      <div className="space-y-1">
                        {content.strategic_recommendations.quick_wins.map((w, i) => (
                          <div key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <span className="text-green-400 shrink-0">&#10003;</span>
                            <span>{w}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {content.strategic_recommendations.content_pillars_suggested?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Suggested Content Pillars</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {content.strategic_recommendations.content_pillars_suggested.map((p, i) => (
                          <div key={i} className="bg-navy-800/50 rounded-lg p-3">
                            <div className="text-sm font-medium text-white">{p.name}</div>
                            <p className="text-xs text-slate-400 mt-1">{p.description}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                              <span>~{p.estimated_articles} articles</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {content.strategic_recommendations.long_term_plays?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-purple-400 uppercase tracking-wider mb-2">Long-Term Plays</h4>
                      <div className="space-y-1">
                        {content.strategic_recommendations.long_term_plays.map((p, i) => (
                          <div key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <span className="text-purple-400 shrink-0">&#9733;</span>
                            <span>{p}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </ReportSection>
              )}

              {/* Citations */}
              {content.citations && content.citations.length > 0 && (
                <ReportSection title="Sources & Citations" icon="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1">
                  <div className="space-y-1">
                    {content.citations.map((c, i) => (
                      <div key={i} className="text-xs text-slate-400 truncate">
                        <span className="text-slate-500 mr-2">[{i + 1}]</span>
                        {c}
                      </div>
                    ))}
                  </div>
                </ReportSection>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Helper Components ---

function ReportSection({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-helm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
        </svg>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function KeywordTable({ label, keywords }: { label: string; keywords?: { keyword: string; intent: string; difficulty: string; priority: string }[] }) {
  if (!keywords || keywords.length === 0) return null;
  return (
    <div className="mb-4">
      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">{label}</h4>
      <div className="bg-navy-800/50 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-navy-700">
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">Keyword</th>
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">Intent</th>
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">Difficulty</th>
              <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">Priority</th>
            </tr>
          </thead>
          <tbody>
            {keywords.map((kw, i) => (
              <tr key={i} className="border-b border-navy-700/30">
                <td className="text-slate-300 px-3 py-1.5 text-xs">{kw.keyword}</td>
                <td className="text-slate-400 px-3 py-1.5 text-xs capitalize">{kw.intent}</td>
                <td className="px-3 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${DIFFICULTY_COLORS[kw.difficulty] || 'text-slate-400'}`}>
                    {kw.difficulty}
                  </span>
                </td>
                <td className={`px-3 py-1.5 text-xs font-medium ${PRIORITY_COLORS[kw.priority] || 'text-slate-400'}`}>
                  {kw.priority}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
